// Development.js
import React, { useMemo, useState, useEffect, useCallback } from "react";
import toast from "react-hot-toast";
import {
  Target,
  Calendar,
  CheckCircle,
  Clock,
  AlertCircle,
  Smile,
  Book,
  Users,
  Heart,
  Eye,
  Edit,
  User as UserIcon,
  ChevronDown,
  ChevronRight,
} from "lucide-react";

import Button from "../UI/Button";
import Milestonemodal from "../Modals/MilestoneModal";
import { UI } from "../UI/uiTokens";
import UpdateMilestoneModal from "../Modals/UpdateMilestoneModal";
import MilestoneDetailsModal from "../Modals/MilestoneDetailsModal";

/* ----------------- Helpers (status + colors) ----------------- */
const getStatusColor = (status) => {
  switch (status) {
    case "Completed":
      return "bg-green-100 text-green-800 dark:bg-green-950/40 dark:text-green-200";
    case "In Progress":
      return "bg-blue-100 text-blue-800 dark:bg-blue-950/40 dark:text-blue-200";
    case "At Risk":
      return "bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-200";
    case "Planned":
    default:
      return "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200";
  }
};

const getStatusIcon = (status) => {
  switch (status) {
    case "Completed":
      return <CheckCircle className="h-4 w-4" />;
    case "In Progress":
      return <Clock className="h-4 w-4" />;
    case "At Risk":
      return <AlertCircle className="h-4 w-4" />;
    default:
      return <Target className="h-4 w-4" />;
  }
};

const getCategoryMeta = (category) => {
  switch (category) {
    case "Physical":
      return {
        label: "Physical",
        icon: Heart,
        chipBg: "bg-red-100 dark:bg-red-950/35",
        chipIcon: "text-red-600 dark:text-red-300",
      };
    case "Educational":
      return {
        label: "Educational",
        icon: Book,
        chipBg: "bg-blue-100 dark:bg-blue-950/35",
        chipIcon: "text-blue-600 dark:text-blue-300",
      };
    case "Social":
      return {
        label: "Social",
        icon: Users,
        chipBg: "bg-green-100 dark:bg-green-950/35",
        chipIcon: "text-green-600 dark:text-green-300",
      };
    case "Emotional":
      return {
        label: "Emotional",
        icon: Smile,
        chipBg: "bg-purple-100 dark:bg-purple-950/35",
        chipIcon: "text-purple-600 dark:text-purple-300",
      };
    default:
      return {
        label: category || "Other",
        icon: Target,
        chipBg: "bg-gray-100 dark:bg-gray-800",
        chipIcon: "text-gray-600 dark:text-gray-300",
      };
  }
};

const clamp = (n, min = 0, max = 100) => Math.max(min, Math.min(max, Number(n || 0)));

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
};

const initials = (name = "") => {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "";
  if (parts.length === 1) return parts[0][0]?.toUpperCase() || "";
  const first = parts[0][0] || "";
  const last = parts[parts.length - 1][0] || "";
  return (first + last).toUpperCase();
};

/* ===========================
NORMALIZERS
=========================== */
const normalizeCategory = (c) => {
  const v = String(c || "").trim().toLowerCase();

  if (v === "physical") return "Physical";
  if (v === "educational" || v === "education" || v === "academic") return "Educational";
  if (v === "social") return "Social";
  if (v === "emotional" || v === "emotion") return "Emotional";

  return v ? v.charAt(0).toUpperCase() + v.slice(1) : "Other";
};

const normalizeMilestone = (m) => ({
  ...m,
  id: m.id ?? m._id,
  childId: Number(m.childId ?? m.child_id ?? m.childID ?? m.child ?? 0),
  category: normalizeCategory(m.category),
  milestone: m.milestone ?? m.title ?? "",
  title: m.title ?? m.milestone ?? "",
  description: m.description ?? "",
  progress: clamp(Number(m.progress ?? 0)),
  status: String(m.status ?? "Planned").trim(),
  targetDate: m.targetDate ?? m.target_date ?? null,
  notes: m.notes ?? "",
  objectives: Array.isArray(m.objectives) ? m.objectives : [],
  child: m.child ?? m.child_name ?? "",

  // ✅ child photo from milestones endpoint (after your backend patch)
  childPhotoUrl: m.childPhotoUrl || m.childPhoto || m.child_photo_url || null,
});

/* ------------------------ Stats Helpers -------------------------- */
const calculateChildStats = (childId, milestones) => {
  const childMilestones = milestones.filter((m) => Number(m.childId) === Number(childId));

  if (childMilestones.length === 0) {
    return {
      overall: 0,
      completedMilestones: 0,
      inProgressMilestones: 0,
      atRiskMilestones: 0,
      physical: 0,
      educational: 0,
      social: 0,
      emotional: 0,
    };
  }

  const avg = (arr) =>
    arr.length ? Math.round(arr.reduce((s, x) => s + clamp(x.progress), 0) / arr.length) : 0;

  const byCat = (cat) => childMilestones.filter((m) => m.category === cat);

  const overall = Math.round(
    childMilestones.reduce((s, m) => s + clamp(m.progress), 0) / childMilestones.length
  );

  return {
    overall: clamp(overall),
    completedMilestones: childMilestones.filter((m) => m.status === "Completed").length,
    inProgressMilestones: childMilestones.filter((m) => m.status === "In Progress").length,
    atRiskMilestones: childMilestones.filter((m) => m.status === "At Risk").length,
    physical: avg(byCat("Physical")),
    educational: avg(byCat("Educational")),
    social: avg(byCat("Social")),
    emotional: avg(byCat("Emotional")),
  };
};

/* ------------------------ UI Components -------------------------- */
const Card = ({ children, className = "" }) => (
  <div
    className={`bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-800 ${className}`}
  >
    {children}
  </div>
);

const CardContent = ({ children, className = "" }) => (
  <div className={`px-5 py-5 ${className}`}>{children}</div>
);

const Badge = ({ className = "", children }) => (
  <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold ${className}`}>
    {children}
  </span>
);

function ProgressBar({ value, height = "h-2" }) {
  const v = clamp(Number(value || 0));
  return (
    <div className={`w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-800 ${height}`}>
      <div
        className="h-full rounded-full bg-gradient-to-r from-purple-500 via-violet-500 to-indigo-500 transition-all shadow-sm"
        style={{ width: `${v}%` }}
      />
    </div>
  );
}

/* ------------------------ Avatar Helpers -------------------------- */
const DEFAULT_CHILD_AVATAR = "/default-avatar.png"; // put this in /public

const safeImg = (src) => {
  const s = String(src || "").trim();
  if (!s || s === "null" || s === "undefined") return "";
  return s;
};

/* ------------------------ Component -------------------------- */
const Development = () => {
  const [openModal, setOpenModal] = useState(false);

  const [children, setChildren] = useState([]);
  const [milestones, setMilestones] = useState([]);

  const [expandedChildren, setExpandedChildren] = useState(new Set());
  const [selectedMilestone, setSelectedMilestone] = useState(null);
  const [editingMilestone, setEditingMilestone] = useState(null);

  const token = localStorage.getItem("admin_token");

  const API_BASE =
    process.env.REACT_APP_API_BASE ||
    (window.location.hostname === "localhost" ? "http://localhost:5000" : "");

  const CHILDREN_OPTIONS_API = `${API_BASE}/api/children/options`;
  const MILESTONES_API = `${API_BASE}/api/milestones`;

  const fetchChildren = useCallback(async () => {
    if (!API_BASE) throw new Error("Missing REACT_APP_API_BASE (set in Vercel).");

    const res = await fetch(CHILDREN_OPTIONS_API, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data?.error || "Failed to fetch children");

    const list = data?.options || [];

    const normalized = list.map((c) => ({
      id: c.id,
      fullName: c.label,
      name: c.label,
      photo: c.photo || null, // backend returns absolute photo URL
    }));

    setChildren(normalized);
  }, [API_BASE, CHILDREN_OPTIONS_API, token]);

  const fetchMilestones = useCallback(async () => {
    if (!API_BASE) throw new Error("Missing REACT_APP_API_BASE (set in Vercel).");

    const res = await fetch(MILESTONES_API, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data?.error || "Failed to fetch milestones");

    const list = Array.isArray(data) ? data : data?.milestones || [];
    setMilestones(list.map(normalizeMilestone));
  }, [API_BASE, MILESTONES_API, token]);

  useEffect(() => {
    (async () => {
      try {
        await fetchChildren();
        await fetchMilestones();
      } catch (e) {
        console.error(e);
      }
    })();
  }, [fetchChildren, fetchMilestones]);

  const handleAddMilestone = () => setOpenModal(true);
  const handleCloseModal = () => setOpenModal(false);

const handleSaveMilestone = async (data) => {
  const toastId = "create-milestone";

  try {
    toast.loading("Creating milestone...", { id: toastId });

    const payload = {
      childId: Number(data.childId),
      category: normalizeCategory(data.category),
      title: data.title,
      description: data.description || "",
      targetDate: data.targetDate || null,
      notes: data.notes || "",
      objectives: Array.isArray(data.objectives) ? data.objectives : [],
    };

    const res = await fetch(MILESTONES_API, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });

    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      console.error("Create milestone failed:", json);
      toast.error(json?.error || "Failed to create milestone", { id: toastId });
      return;
    }

    await fetchMilestones();
    setOpenModal(false);
    toast.success("Milestone created successfully", { id: toastId });
  } catch (err) {
    console.error(err);
    toast.error("Failed to create milestone", { id: toastId });
  }
};

const saveUpdate = async () => {
  if (!editingMilestone?.milestone || !editingMilestone?.category) {
    toast.error("Please complete the required milestone fields.");
    return;
  }

  const toastId = "update-milestone";

  try {
    toast.loading("Saving milestone...", { id: toastId });

    const payload = {
      ...editingMilestone,
      category: normalizeCategory(editingMilestone.category),
      progress: clamp(editingMilestone.progress),
    };

    const res = await fetch(`${MILESTONES_API}/${editingMilestone.id}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });

    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      console.error("Update milestone failed:", json);
      toast.error(json?.error || "Failed to update milestone", { id: toastId });
      return;
    }

    await fetchMilestones();
    setEditingMilestone(null);
    toast.success("Milestone updated successfully", { id: toastId });
  } catch (err) {
    console.error(err);
    toast.error("Failed to update milestone", { id: toastId });
  }
};

  const totals = useMemo(() => {
    const totalChildren = children.length;
    const totalMilestones = milestones.length;
    const completed = milestones.filter((m) => m.status === "Completed").length;
    const atRisk = milestones.filter((m) => m.status === "At Risk").length;
    return { totalChildren, totalMilestones, completed, atRisk };
  }, [milestones, children]);

  // ✅ build map from milestone payload (childPhotoUrl) so we can fallback if children/options has no photo
  const photoByChildId = useMemo(() => {
    const map = new Map();
    for (const m of milestones) {
      const id = Number(m.childId);
      const photo = safeImg(m.childPhotoUrl);
      if (id && photo && !map.has(id)) map.set(id, photo);
    }
    return map;
  }, [milestones]);

  // ✅ SHOW ALL CHILDREN (with photo fallback)
  const childrenToShow = useMemo(() => {
    return children.map((c) => ({
      ...c,
      photo: safeImg(c.photo) || photoByChildId.get(Number(c.id)) || null,
    }));
  }, [children, photoByChildId]);

  const toggleChildExpansion = (childId) => {
    setExpandedChildren((prev) => {
      const next = new Set(prev);
      if (next.has(childId)) next.delete(childId);
      else next.add(childId);
      return next;
    });
  };

  const openDetails = (milestone) => setSelectedMilestone(milestone);

  // ✅ pass child photo into update modal (in case older milestones don't include it)
  const openUpdate = (milestone, child) => {
    const merged = {
      ...milestone,
      child: milestone.child || child?.name || milestone.child_name || "—",
      childPhotoUrl: milestone.childPhotoUrl || child?.photo || null,
    };
    setEditingMilestone(merged);
  };

  const updateEditingObjective = (index, value) => {
    setEditingMilestone((prev) => ({
      ...prev,
      objectives: (prev?.objectives || []).map((o, i) => (i === index ? value : o)),
    }));
  };

  const addEditingObjective = () => {
    setEditingMilestone((prev) => ({
      ...prev,
      objectives: [...(prev?.objectives || []), ""],
    }));
  };

  const removeEditingObjective = (index) => {
    setEditingMilestone((prev) => ({
      ...prev,
      objectives: (prev?.objectives || []).filter((_, i) => i !== index),
    }));
  };

  const actionBtn =
    UI?.btnSmallOutline ||
    "inline-flex items-center gap-2 rounded-xl border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-gray-800 hover:bg-gray-50 " +
      "dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:hover:bg-gray-800";

  return (
    <div className="p-6 bg-gray-50 dark:bg-gray-950 min-h-screen space-y-6 transition-colors duration-300">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100">
            Development Tracking
          </h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Track milestones and monitor each child’s progress
          </p>
        </div>

        <Button onClick={handleAddMilestone} variant="primary" type="button" className="px-6 py-2.5">
          Add Development Record
        </Button>
      </div>

      {/* Summary Statistics */}
      <div className="grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-4">
        <Card className="hover:shadow-lg transition-shadow duration-200 border-l-4 border-l-blue-500 dark:border-l-blue-400">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-xs sm:text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                  Total Children
                </p>
                <p className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-1">
                  {totals.totalChildren}
                </p>
                <p className="text-xs sm:text-sm text-blue-600 dark:text-blue-400 font-medium">
                  In Children module
                </p>
              </div>
              <div className="bg-gradient-to-br from-blue-500 to-indigo-600 p-3 sm:p-4 rounded-2xl shadow-lg">
                <UserIcon className="h-6 w-6 sm:h-7 sm:w-7 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-lg transition-shadow duration-200 border-l-4 border-l-purple-500 dark:border-l-purple-400">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-xs sm:text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                  Total Milestones
                </p>
                <p className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-1">
                  {totals.totalMilestones}
                </p>
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 font-medium">
                  Being tracked
                </p>
              </div>
              <div className="bg-gradient-to-br from-purple-500 to-violet-600 p-3 sm:p-4 rounded-2xl shadow-lg">
                <Target className="h-6 w-6 sm:h-7 sm:w-7 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-lg transition-shadow duration-200 border-l-4 border-l-green-500 dark:border-l-green-400">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-xs sm:text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                  Completed
                </p>
                <p className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-1">
                  {totals.completed}
                </p>
                <p className="text-xs sm:text-sm text-green-600 dark:text-green-400 font-medium">
                  Achievements
                </p>
              </div>
              <div className="bg-gradient-to-br from-green-500 to-emerald-600 p-3 sm:p-4 rounded-2xl shadow-lg">
                <CheckCircle className="h-6 w-6 sm:h-7 sm:w-7 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-lg transition-shadow duration-200 border-l-4 border-l-red-500 dark:border-l-red-400">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-xs sm:text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                  At Risk
                </p>
                <p className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-1">
                  {totals.atRisk}
                </p>
                <p className="text-xs sm:text-sm text-red-600 dark:text-red-400 font-medium">
                  Need attention
                </p>
              </div>
              <div className="bg-gradient-to-br from-red-500 to-rose-600 p-3 sm:p-4 rounded-2xl shadow-lg">
                <AlertCircle className="h-6 w-6 sm:h-7 sm:w-7 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Children List */}
      <div className="space-y-4">
        {childrenToShow.map((child) => {
          const childMilestones = milestones.filter((m) => Number(m.childId) === Number(child.id));
          const stats = calculateChildStats(child.id, milestones);
          const isExpanded = expandedChildren.has(child.id);

          return (
            <Card key={child.id} className="overflow-hidden hover:shadow-md transition-shadow duration-200">
              {/* Child Header */}
              <div
                className="cursor-pointer px-5 py-4 transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/50"
                onClick={() => toggleChildExpansion(child.id)}
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-4 min-w-0">
                    <button
                      className="shrink-0 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-2 hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200"
                      type="button"
                    >
                      {isExpanded ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
                    </button>

                    {/* ✅ CHILD PHOTO (perfect circle on mobile) */}
                    <div className="w-10 h-10 sm:w-12 sm:h-12 aspect-square rounded-full overflow-hidden shrink-0 bg-gray-100 dark:bg-gray-800">
                      {safeImg(child.photo) ? (
                        <img
                          src={safeImg(child.photo)}
                          alt={child.name}
                          className="w-full h-full object-cover block"
                          loading="lazy"
                          onError={(e) => {
                            e.currentTarget.src = DEFAULT_CHILD_AVATAR;
                          }}
                        />
                      ) : (
                        <div className="w-full h-full grid place-items-center text-sm font-semibold text-gray-700 dark:text-gray-200">
                          {initials(child.name)}
                        </div>
                      )}
                    </div>

                    <div className="min-w-0">
                      <h3 className="font-semibold text-gray-900 dark:text-gray-100 truncate">{child.name}</h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                        {childMilestones.length} milestone{childMilestones.length !== 1 ? "s" : ""}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-6">
                    <div className="text-right">
                      <p className="text-sm text-gray-600 dark:text-gray-400">Overall Progress</p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{stats.overall}%</p>
                    </div>

                    <div className="hidden lg:flex gap-2">
                      <Badge className="bg-green-100 text-green-800 dark:bg-green-950/40 dark:text-green-200">
                        {stats.completedMilestones} Completed
                      </Badge>
                      <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-950/40 dark:text-blue-200">
                        {stats.inProgressMilestones} In Progress
                      </Badge>
                      {stats.atRiskMilestones > 0 && (
                        <Badge className="bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-200">
                          {stats.atRiskMilestones} At Risk
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>

                {/* badges for smaller screens */}
                <div className="mt-3 flex flex-wrap gap-2 lg:hidden">
                  <Badge className="bg-green-100 text-green-800 dark:bg-green-950/40 dark:text-green-200">
                    {stats.completedMilestones} Completed
                  </Badge>
                  <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-950/40 dark:text-blue-200">
                    {stats.inProgressMilestones} In Progress
                  </Badge>
                  {stats.atRiskMilestones > 0 && (
                    <Badge className="bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-200">
                      {stats.atRiskMilestones} At Risk
                    </Badge>
                  )}
                </div>
              </div>

              {/* Expanded Content */}
              {isExpanded && (
                <div className="border-t border-gray-100 dark:border-gray-800 bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 px-5 py-5">
                  {/* Development Areas */}
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-semibold text-gray-900 dark:text-gray-100">Development Areas</h4>
                      <span className="text-xs text-gray-500 dark:text-gray-400">Average progress per category</span>
                    </div>

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
                      {[
                        { name: "Physical", value: stats.physical },
                        { name: "Educational", value: stats.educational },
                        { name: "Social", value: stats.social },
                        { name: "Emotional", value: stats.emotional },
                      ].map((area) => {
                        const meta = getCategoryMeta(area.name);
                        const Icon = meta.icon;

                        return (
                          <div
                            key={area.name}
                            className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 shadow-sm"
                          >
                            <div className="mb-2 flex items-center gap-2">
                              <div className={`rounded-xl ${meta.chipBg} p-2`}>
                                <Icon className={`h-4 w-4 ${meta.chipIcon}`} />
                              </div>
                              <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">{meta.label}</span>
                            </div>

                            <div className="space-y-1">
                              <div className="flex items-center justify-between">
                                <span className="text-xs text-gray-600 dark:text-gray-400">Progress</span>
                                <span className="text-sm font-bold text-gray-900 dark:text-gray-100">{area.value}%</span>
                              </div>
                              <ProgressBar value={area.value} height="h-2" />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Milestones */}
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-semibold text-gray-900 dark:text-gray-100">Milestones</h4>
                      <span className="text-xs text-gray-500 dark:text-gray-400">{childMilestones.length} total</span>
                    </div>

                    {childMilestones.length === 0 ? (
                      <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6 text-sm text-gray-600 dark:text-gray-400 shadow-sm">
                        No milestones for this child yet.
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                        {childMilestones.map((m) => {
                          const meta = getCategoryMeta(m.category);
                          const Icon = meta.icon;

                          return (
                            <div
                              key={m.id}
                              className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 shadow-sm hover:shadow-md transition-shadow"
                            >
                              <div className="mb-3 flex items-start justify-between gap-3">
                                <div className="flex items-start gap-2 min-w-0">
                                  {/* optional small avatar per milestone */}
                                  <div className="w-8 h-8 rounded-full overflow-hidden shrink-0 bg-gray-100 dark:bg-gray-800">
                                    {safeImg(m.childPhotoUrl || child.photo) ? (
                                      <img
                                        src={safeImg(m.childPhotoUrl || child.photo)}
                                        alt={child.name}
                                        className="w-full h-full object-cover block"
                                        loading="lazy"
                                        onError={(e) => (e.currentTarget.src = DEFAULT_CHILD_AVATAR)}
                                      />
                                    ) : (
                                      <div className="w-full h-full grid place-items-center text-[10px] font-semibold text-gray-700 dark:text-gray-200">
                                        {initials(child.name)}
                                      </div>
                                    )}
                                  </div>

                                  <div className={`mt-0.5 rounded-xl ${meta.chipBg} p-2`}>
                                    <Icon className={`h-4 w-4 ${meta.chipIcon}`} />
                                  </div>

                                  <div className="min-w-0">
                                    <h5 className="font-semibold text-gray-900 dark:text-gray-100 truncate">
                                      {m.milestone}
                                    </h5>
                                    <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">{m.category}</p>
                                  </div>
                                </div>

                                <span
                                  className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold ${getStatusColor(
                                    m.status
                                  )}`}
                                >
                                  {getStatusIcon(m.status)}
                                  <span>{m.status}</span>
                                </span>
                              </div>

                              <div className="space-y-2">
                                <div>
                                  <div className="mb-1 flex items-center justify-between">
                                    <span className="text-xs text-gray-600 dark:text-gray-400">Progress</span>
                                    <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                      {clamp(m.progress)}%
                                    </span>
                                  </div>
                                  <ProgressBar value={m.progress} height="h-2" />
                                </div>

                                {m.targetDate && (
                                  <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                                    <Calendar className="h-3 w-3" />
                                    <span>Due: {formatDate(m.targetDate)}</span>
                                  </div>
                                )}

                                {m.notes && (
                                  <p className="rounded-xl bg-gray-50 dark:bg-gray-950/40 border border-gray-200 dark:border-gray-800 p-3 text-xs text-gray-700 dark:text-gray-200">
                                    {m.notes}
                                  </p>
                                )}

                                <div className="flex gap-2 pt-2">
                                  <button onClick={() => openDetails(m)} className={actionBtn} type="button">
                                    <Eye className="h-3 w-3" />
                                    Details
                                  </button>

                                  <button onClick={() => openUpdate(m, child)} className={actionBtn} type="button">
                                    <Edit className="h-3 w-3" />
                                    Update
                                  </button>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {/* Add Milestone Modal */}
      {openModal && <Milestonemodal onClose={handleCloseModal} onSave={handleSaveMilestone} children={childrenToShow} />}

      {/* Details Modal */}
      {selectedMilestone && (
        <MilestoneDetailsModal
          UI={UI}
          selectedMilestone={selectedMilestone}
          closeDetails={() => setSelectedMilestone(null)}
          initials={initials}
          getCategoryMeta={getCategoryMeta}
          getStatusColor={getStatusColor}
          getStatusIcon={getStatusIcon}
          formatDate={formatDate}
          clamp={clamp}
          ProgressBar={ProgressBar}
        />
      )}

      {/* Update Modal */}
      <UpdateMilestoneModal
        UI={UI}
        editingMilestone={editingMilestone}
        setEditingMilestone={setEditingMilestone}
        closeUpdate={() => setEditingMilestone(null)}
        saveUpdate={saveUpdate}
        clamp={clamp}
        ProgressBar={ProgressBar}
        updateEditingObjective={updateEditingObjective}
        addEditingObjective={addEditingObjective}
        removeEditingObjective={removeEditingObjective}
      />
    </div>
  );
};

export default Development;